import * as React from "react"
import CircularProgress from "@mui/material/CircularProgress"
import Button, { ButtonProps } from "@mui/material/Button"
import { styled } from "@mui/material/styles"
import { Icon, Typography, Box } from "@mui/material"
import { TypographyVariant } from "types/material/typography"

interface OreveButtonProps extends ButtonProps {
    loading?: boolean
    value?: string
    icon?: React.ElementType | null
    typography?: TypographyVariant
    removeMargin?: boolean
    gapSize?: string | null
}
// Custom button styles
const StyledButton = styled(Button)<ButtonProps>({
    position: "relative",
    borderRadius: "100px",
    minWidth: "0",
    padding: 0,
    textTransform: "none",
})

// Custom button component
export const OreveButton = ({
    loading = false,
    variant = "contained",
    color = "primary",
    icon = null,
    value = "",
    removeMargin = false,
    typography = "labelLarge",
    gapSize = null,

    sx = {},
    ...otherProps
}: OreveButtonProps) => {
    const outlinedSx = {
        "&:hover, &:focus": {
            color: color + ".hover",
            borderColor: color + ".hover",
            backgroundColor: "transparent",
        },
        "&:disabled": {
            backgroundColor: "grey.100",
            color: "grey.400",
            borderColor: "transparent",
        },
    }

    const containedSx = {
        "&:hover, &:focus": {
            backgroundColor: color + ".hover",
        },
        "&:disabled": {
            backgroundColor: "grey.100",
            color: "grey.400",
            borderColor: "transparent",
        },
    }

    const textSx = {
        textDecoration: "underline",
        textUnderlineOffset: "0.3em",
        "&:hover, &:focus": {
            color: color + ".hover",
            backgroundColor: "transparent",
            textDecoration: "underline",
        },
        "&:disabled": {
            color: "grey.400",
        },
    }

    let buttonSx: object = containedSx
    switch (variant) {
        case "outlined":
            buttonSx = outlinedSx
            break
        case "text":
            buttonSx = textSx
            break
    }


    return (
        <StyledButton
            {...otherProps}
            disabled={loading || otherProps.disabled}
            variant={variant}
            color={color}
            sx={{
                ...buttonSx,
                ...sx,
                width: "fit-content",
            }}
        >
            <Typography variant={typography} color="inherit">
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: gapSize ? gapSize : "1em",
                        ...(removeMargin
                            ? {}
                            : value === ""
                              ? { margin: "1em" }
                              : { margin: "0.7em 1.5em 0.7em 1.5em" }),
                    }}
                >
                    {icon && <Icon
                        sx={{
                            fontSize: "1em",
                            transform: "scale(1.5)",
                        }}
                        component={icon}
                    />}

                    {value !== "" && value}
                </Box>
            </Typography>

            {loading && (
                <CircularProgress
                    size={24}
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-12px",
                        marginLeft: "-12px",
                        color: color + ".main",
                    }}
                />
            )}
        </StyledButton>
    )
}
